import {z} from 'zod';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {FormProvider, useForm} from 'react-hook-form';
import {VerdocsEndpoint, getOrganizationPipelineSettings, updateOrganizationPipelineSettings} from '@verdocs/js-sdk';
import {CheckboxInput, Divider, PageTitle, Spinner, FlexFill, HelpDialog} from '../../Components';
import {AppState} from '../../AppState';

const schema = z.object({
  process_tags: z.boolean(),
  process_acroforms: z.boolean(),
  ignore_invalid_roles: z.boolean(),
  ignore_invalid_fields: z.boolean(),
});

type SchemaType = z.infer<typeof schema>;

const EMPTY: SchemaType = {process_tags: false, process_acroforms: false, ignore_invalid_roles: false, ignore_invalid_fields: false};

export const PipelineSettings: FC = observer(() => {
  const [submitting, setSubmitting] = useState(false);
  const organizationId = AppState.currentProfile?.organization_id || '';

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => (organizationId ? getOrganizationPipelineSettings(VerdocsEndpoint.getDefault(), organizationId) : EMPTY),
  });

  const handleSubmit = (values: SchemaType) => {
    setSubmitting(true);
    updateOrganizationPipelineSettings(VerdocsEndpoint.getDefault(), organizationId, values)
      .then(() => {
        setSubmitting(false);
        toast.success('Settings saved');
      })
      .catch((e) => {
        setSubmitting(false);
        toast.error('Error saving document processing settings: ' + (e.response?.data?.error || e.message));
      });
  };

  if (form.formState.isLoading) {
    return (
      <div className="p-4 h-full overflow-y-scroll">
        <PageTitle>Document Processing</PageTitle>
        <div className="mt-4" />
        <Spinner mode="dark" />
      </div>
    );
  }

  return (
    <div className="">
      <PageTitle>
        Document Processing
        <FlexFill />
        <HelpDialog title="Document Processing">
          <p>
            These settings control how Verdocs automatically turns the documents you upload into signable fields. They apply
            organization-wide to every document processed for your organization (both templates and directly-created envelopes).
          </p>
        </HelpDialog>
      </PageTitle>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div className="col-span-full">
              <h3 className="text-lg font-semibold text-slate-800">Field Detection</h3>
              <Divider />
            </div>

            <CheckboxInput
              label="Process text tags ({{...}})"
              name="process_tags"
              explainer="Scan uploaded documents for {{...}} text tags and convert them into signable fields automatically. Turn this off if your documents legitimately contain text that looks like a tag and should be left untouched."
            />

            <CheckboxInput
              label="Detect fillable PDF (AcroForm) fields"
              name="process_acroforms"
              explainer="When a document has no text tags, automatically detect existing fillable PDF form fields (AcroForm widgets) and convert them into Verdocs fields. Handy for standard fillable PDFs, but can produce unexpected fields when a document also has manually-placed ones."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div className="col-span-full">
              <h3 className="text-lg font-semibold text-slate-800">Validation</h3>
              <Divider />
            </div>

            <CheckboxInput
              label="Skip invalid roles"
              name="ignore_invalid_roles"
              explainer="When detecting fields from a document, silently skip any tag whose recipient role name is missing or invalid. By default, if invalid roles are detected an error will be thrown."
            />

            <CheckboxInput
              label="Skip invalid fields"
              name="ignore_invalid_fields"
              explainer="When detecting fields from a document, silently skip any tag that does not form a valid field (unknown field type, missing role, and similar). By default, if invalid fields are detected an error will be thrown."
            />
          </div>

          <div>
            <VerdocsButton type="submit" label="Update Settings" size="normal" disabled={submitting} />
          </div>
        </form>
      </FormProvider>
    </div>
  );
});
